<!--
  Mounts the SVG <symbol> sprite once near the root of the app.
  Reference icons via <RIcon name="home" /> which uses <use href="#i-home" />.
-->
<script setup lang="ts"></script>

<template>
  <svg
    width="0"
    height="0"
    class="absolute"
    aria-hidden="true"
  >
    <defs>
      <symbol
        id="i-home"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
          d="M2.5 7.5 8 3l5.5 4.5V13a.5.5 0 0 1-.5.5H9.5V10h-3v3.5H3a.5.5 0 0 1-.5-.5V7.5Z"
        />
      </symbol>
      <!-- Deux silhouettes, épaules alignées : une troupe (trois se confondent en 18 px) -->
      <symbol
        id="i-troupe"
        viewBox="0 0 16 16"
      >
        <circle
          cx="5.8"
          cy="5.5"
          r="2.2"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <circle
          cx="11.2"
          cy="6.2"
          r="1.8"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M1.8 13.5c.3-2.2 1.8-3.6 4-3.6s3.7 1.4 4 3.6M10.2 10.2c.3-.1.6-.1 1-.1 1.8 0 3 1.2 3.3 3.4"
        />
      </symbol>
      <!-- Manuscrit, coin plié : une pièce est un texte -->
      <symbol
        id="i-book"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M4 1.8h5.6l3 3v8.9a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V2.3a.5.5 0 0 1 .5-.5Z"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M9.5 1.8v3h3M5.8 7.6h4.4M5.8 9.8h4.4M5.8 12h2.6"
        />
      </symbol>
      <!-- Rideau de scène : le spectacle monté -->
      <symbol
        id="i-prod"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M2 2.5h12M2.8 2.5v10.3c1.6 0 2.8-1 3.3-3.6.3-1.9.4-4.2.4-6.7M13.2 2.5v10.3c-1.6 0-2.8-1-3.3-3.6-.3-1.9-.4-4.2-.4-6.7M1.5 13.5h13"
        />
      </symbol>
      <symbol
        id="i-cal"
        viewBox="0 0 16 16"
      >
        <rect
          x="2.5"
          y="4"
          width="11"
          height="9"
          rx="1.5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M5 2.5v3M11 2.5v3M2.5 7.5h11"
        />
      </symbol>
      <!-- Répétitions régulières : deux flèches en boucle -->
      <symbol
        id="i-repeat"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M11.3 1.3 14 4l-2.7 2.7M2 7.3v-.6A2.7 2.7 0 0 1 4.7 4H14M4.7 14.7 2 12l2.7-2.7M14 8.7v.6a2.7 2.7 0 0 1-2.7 2.7H2"
        />
      </symbol>
      <!-- Renouveler (adresse d'agenda) : une flèche en cercle, distincte des deux flèches des répétitions régulières -->
      <symbol
        id="i-refresh"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M14 8a6 6 0 1 1-6-6c1.68 0 3.29.67 4.49 1.83L14 5.33M14 2v3.33h-3.33"
        />
      </symbol>
      <symbol
        id="i-clock"
        viewBox="0 0 16 16"
      >
        <circle
          cx="8"
          cy="8"
          r="5.75"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M8 5v3.25l2 1.25"
        />
      </symbol>
      <symbol
        id="i-people"
        viewBox="0 0 16 16"
      >
        <circle
          cx="8"
          cy="6"
          r="2.5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          d="M3 13c.6-2.2 2.6-3.5 5-3.5s4.4 1.3 5 3.5"
        />
      </symbol>
      <symbol
        id="i-arrowUpDown"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M5 13V3M2.5 5.5 5 3l2.5 2.5M11 3v10M8.5 10.5 11 13l2.5-2.5"
        />
      </symbol>
      <symbol
        id="i-arrow"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M3 8h10M9 4l4 4-4 4"
        />
      </symbol>
      <symbol
        id="i-arrowUp"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M8 13V3M4 7l4-4 4 4"
        />
      </symbol>
      <symbol
        id="i-bell"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M8 2.5c2.2 0 4 1.8 4 4v2.5l1 2H3l1-2V6.5c0-2.2 1.8-4 4-4ZM6.5 13a1.5 1.5 0 0 0 3 0"
        />
      </symbol>
      <symbol
        id="i-search"
        viewBox="0 0 16 16"
      >
        <circle
          cx="7"
          cy="7"
          r="3.5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="m10 10 3 3"
        />
      </symbol>
      <symbol
        id="i-chev"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="m6 4 4 4-4 4"
        />
      </symbol>
      <symbol
        id="i-back"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="m10 4-4 4 4 4"
        />
      </symbol>
      <symbol
        id="i-play"
        viewBox="0 0 16 16"
      >
        <path
          fill="currentColor"
          d="M5 3.5v9l8-4.5z"
        />
      </symbol>
      <symbol
        id="i-eye"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
          d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8Z"
        />
        <circle
          cx="8"
          cy="8"
          r="2"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
      </symbol>
      <symbol
        id="i-plus"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M8 3v10M3 8h10"
        />
      </symbol>
      <symbol
        id="i-dots"
        viewBox="0 0 16 16"
      >
        <circle
          cx="3.5"
          cy="8"
          r="1.2"
          fill="currentColor"
        />
        <circle
          cx="8"
          cy="8"
          r="1.2"
          fill="currentColor"
        />
        <circle
          cx="12.5"
          cy="8"
          r="1.2"
          fill="currentColor"
        />
      </symbol>
      <symbol
        id="i-dots-v"
        viewBox="0 0 16 16"
      >
        <circle
          cx="8"
          cy="3.5"
          r="1.2"
          fill="currentColor"
        />
        <circle
          cx="8"
          cy="8"
          r="1.2"
          fill="currentColor"
        />
        <circle
          cx="8"
          cy="12.5"
          r="1.2"
          fill="currentColor"
        />
      </symbol>
      <symbol
        id="i-pin"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
          d="m6 2 5 5-2 1 1 3-3-3-2 2-1-3-1-1 3-2 0-2Z"
        />
      </symbol>
      <symbol
        id="i-mic"
        viewBox="0 0 16 16"
      >
        <rect
          x="6"
          y="2.5"
          width="4"
          height="7"
          rx="2"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M3.5 8a4.5 4.5 0 0 0 9 0M8 12.5v1.5"
        />
      </symbol>
      <symbol
        id="i-grid"
        viewBox="0 0 16 16"
      >
        <rect
          x="2"
          y="2"
          width="5"
          height="5"
          rx="1"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <rect
          x="9"
          y="2"
          width="5"
          height="5"
          rx="1"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <rect
          x="2"
          y="9"
          width="5"
          height="5"
          rx="1"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <rect
          x="9"
          y="9"
          width="5"
          height="5"
          rx="1"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
      </symbol>
      <symbol
        id="i-list"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M5 4h8M5 8h8M5 12h8M2.5 4h.01M2.5 8h.01M2.5 12h.01"
        />
      </symbol>
      <symbol
        id="i-edit"
        viewBox="0 0 24 24"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
        />
      </symbol>
      <symbol
        id="i-check"
        viewBox="0 0 24 24"
      >
        <polyline
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          points="20 6 9 17 4 12"
        />
      </symbol>
      <symbol
        id="i-x"
        viewBox="0 0 24 24"
      >
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="18"
          y1="6"
          x2="6"
          y2="18"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="6"
          y1="6"
          x2="18"
          y2="18"
        />
      </symbol>
      <!-- Point d'interrogation cerclé : l'aide -->
      <symbol
        id="i-aide"
        viewBox="0 0 16 16"
      >
        <circle
          cx="8"
          cy="8"
          r="6"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M6.2 6.3a1.9 1.9 0 0 1 3.7.5c0 1.3-1.9 1.6-1.9 2.8"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M8 11.4v.1"
        />
      </symbol>
      <symbol
        id="i-settings"
        viewBox="0 0 24 24"
      >
        <circle
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          cx="12"
          cy="12"
          r="3"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"
        />
      </symbol>
      <symbol
        id="i-building"
        viewBox="0 0 24 24"
      >
        <rect
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x="2"
          y="7"
          width="20"
          height="14"
          rx="2"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"
        />
      </symbol>
      <symbol
        id="i-logout"
        viewBox="0 0 24 24"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"
        />
        <polyline
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          points="16 17 21 12 16 7"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="21"
          y1="12"
          x2="9"
          y2="12"
        />
      </symbol>
      <symbol
        id="i-key"
        viewBox="0 0 24 24"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"
        />
      </symbol>
      <symbol
        id="i-invite"
        viewBox="0 0 24 24"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"
        />
        <circle
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          cx="8.5"
          cy="7"
          r="4"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="20"
          y1="8"
          x2="20"
          y2="14"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="23"
          y1="11"
          x2="17"
          y2="11"
        />
      </symbol>
      <symbol
        id="i-notebookPen"
        viewBox="0 0 24 24"
      >
        <!-- Carnet et stylo (Lucide notebook-pen) : la conduite, notes de jeu -->
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          d="M2 6h4M2 10h4M2 14h4M2 18h4"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"
        />
      </symbol>
      <symbol
        id="i-film"
        viewBox="0 0 24 24"
      >
        <rect
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x="2"
          y="2"
          width="20"
          height="20"
          rx="2.18"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="7"
          y1="2"
          x2="7"
          y2="22"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="17"
          y1="2"
          x2="17"
          y2="22"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="2"
          y1="12"
          x2="22"
          y2="12"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="2"
          y1="7"
          x2="7"
          y2="7"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="2"
          y1="17"
          x2="7"
          y2="17"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="17"
          y1="17"
          x2="22"
          y2="17"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="17"
          y1="7"
          x2="22"
          y2="7"
        />
      </symbol>
      <symbol
        id="i-loader"
        viewBox="0 0 24 24"
      >
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="12"
          y1="2"
          x2="12"
          y2="6"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="12"
          y1="18"
          x2="12"
          y2="22"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="4.93"
          y1="4.93"
          x2="7.76"
          y2="7.76"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="16.24"
          y1="16.24"
          x2="19.07"
          y2="19.07"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="2"
          y1="12"
          x2="6"
          y2="12"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="18"
          y1="12"
          x2="22"
          y2="12"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="4.93"
          y1="19.07"
          x2="7.76"
          y2="16.24"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          x1="16.24"
          y1="7.76"
          x2="19.07"
          y2="4.93"
        />
      </symbol>
      <symbol
        id="i-menu"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M2 4h12M2 8h12M2 12h12"
        />
      </symbol>
      <symbol
        id="i-invitation"
        viewBox="0 0 16 16"
      >
        <rect
          x="2"
          y="4"
          width="12"
          height="9"
          rx="1.5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M2 5.5l6 4.5 6-4.5"
        />
      </symbol>
      <symbol
        id="i-debrief"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M3 2.5h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H9l-3 2v-2H3a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
        />
      </symbol>
      <symbol
        id="i-image"
        viewBox="0 0 16 16"
      >
        <rect
          x="2"
          y="2.5"
          width="12"
          height="11"
          rx="1.5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <circle
          cx="5.5"
          cy="6"
          r="1.2"
          fill="currentColor"
        />
        <polyline
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          points="2,11 5.5,7.5 8,10 10,8.5 14,11.5"
        />
      </symbol>
      <symbol
        id="i-upload"
        viewBox="0 0 16 16"
      >
        <polyline
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          points="5.5,6 8,3 10.5,6"
        />
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          x1="8"
          y1="3"
          x2="8"
          y2="10.5"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M3 13h10"
        />
      </symbol>
      <symbol
        id="i-trash"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M2.5 4.5h11M6 4.5V3.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5v1"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M4.5 4.5l.5 8a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l.5-8"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M6.5 7v4M9.5 7v4"
        />
      </symbol>
      <symbol
        id="i-copy"
        viewBox="0 0 16 16"
      >
        <rect
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          x="5.5"
          y="5.5"
          width="8"
          height="8"
          rx="1"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2"
        />
      </symbol>
      <symbol
        id="i-book-open"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M8 4.5C6.5 3.3 4.5 3 2 3v9.5c2.5 0 4.5.3 6 1.5 1.5-1.2 3.5-1.5 6-1.5V3c-2.5 0-4.5.3-6 1.5ZM8 4.5V14"
        />
      </symbol>
      <symbol
        id="i-pause"
        viewBox="0 0 16 16"
      >
        <rect
          x="4.5"
          y="3.5"
          width="2.5"
          height="9"
          rx="1"
          fill="currentColor"
        />
        <rect
          x="9"
          y="3.5"
          width="2.5"
          height="9"
          rx="1"
          fill="currentColor"
        />
      </symbol>
      <symbol
        id="i-volume2"
        viewBox="0 0 16 16"
      >
        <polygon
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
          points="2,6 5.5,6 9.5,3.5 9.5,12.5 5.5,10 2,10"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M11 6.5a3 3 0 0 1 0 3"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M12.5 4.5a6 6 0 0 1 0 7"
        />
      </symbol>
      <symbol
        id="i-volumeX"
        viewBox="0 0 16 16"
      >
        <polygon
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
          points="2,6 5.5,6 9.5,3.5 9.5,12.5 5.5,10 2,10"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M11.5 6.5l3 3M14.5 6.5l-3 3"
        />
      </symbol>
      <symbol
        id="i-video"
        viewBox="0 0 16 16"
      >
        <rect
          x="1.5"
          y="5"
          width="9"
          height="6"
          rx="1.5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
          d="M10.5 7.5l4.5-2v5l-4.5-2"
        />
      </symbol>
      <symbol
        id="i-fileText"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
          d="M10 2H4.5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V5z"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
          d="M10 2v3h2.5"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M5.5 8.5h5M5.5 11h3.5"
        />
      </symbol>
      <symbol
        id="i-messageCircle"
        viewBox="0 0 16 16"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M14 7.5a6 6 0 0 1-6 6c-1.1 0-2.1-.3-3-.8L2 13.5l.8-3A6 6 0 0 1 8 1.5a6 6 0 0 1 6 6Z"
        />
      </symbol>
      <symbol
        id="i-download"
        viewBox="0 0 16 16"
      >
        <line
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          x1="8"
          y1="2"
          x2="8"
          y2="10"
        />
        <polyline
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          points="5 7.5 8 10.5 11 7.5"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          d="M2.5 12.5h11"
        />
      </symbol>
    </defs>
  </svg>
</template>
